import { AgGridReact } from "@ag-grid-community/react";
import { Box, Flex, HStack, Skeleton, Stack } from "@chakra-ui/react";
import React from "react";
import CoreDataGridContainer, {
  IRegistryColDef,
} from "../../../components/datagrid/CoreDataGridContainer";
import GeneMapDateBar from "../../../components/datagrid/GeneMapDateBar";
import { QuickNavigator } from "../../../components/datagrid/QuickNavigator";
import RegistryFilterMode from "../../../components/datagrid/RegistryFilterMode";
import RegistryLeaf from "../../../components/datagrid/RegistryLeaf";
import RegistryPane from "../../../components/datagrid/RegistryPane";
import RegistryToolbar from "../../../components/datagrid/RegistryToolbar";
import {
  IRegistryDataColumn,
  RegistryDataColumnTypeEnum,
} from "../../../models/RegistryDataColumn";
import { useReadRegistryData } from "../../../services/api/data/queries/useReadRegistryData";
import useReadRegistry from "../../../services/api/registries/queries/useReadRegistry";
import { useSetHomeState } from "../stores/actions/useSetHomeState";
import { useSetIsAddMode } from "../stores/actions/useSetIsAddMode";
import { useSetTypeGeneMap } from "../stores/actions/useSetTypeGeneMap";
import { useSelectIsAddMode } from "../stores/select/useSelectIsAddMode";
import { useSelectIsCaseView } from "../stores/select/useSelectIsCaseView";
import { useSelectIsEditMode } from "../stores/select/useSelectIsEditMode";
import { useSelectIsGeneMapView } from "../stores/select/useSelectIsGeneMapView";
import { useSelectIsPaneOpen } from "../stores/select/useSelectIsPaneOpen";
import { GeneTypeEnum } from "../util/geneChart";

import { AddRecordModal } from "./AddRecordModal";
import RegistryEditMode from "./RegistryEditMode";

interface IProps {
  registryId: string;
}

const RegistryDataGrid = ({ registryId }: IProps) => {
  const gridRef: any = React.useRef<AgGridReact>(); // Optional - for accessing Grid's API

  const [isLeafOpen, set_isLeafOpen] = React.useState(false);
  const [isGridReady, setIsGridReady] = React.useState(false);

  const isPaneOpen = useSelectIsPaneOpen();
  const isGeneMapView = useSelectIsGeneMapView();
  const isCaseView = useSelectIsCaseView();
  const isEditMode = useSelectIsEditMode();
  const isAddMode = useSelectIsAddMode();
  const setIsAddMode = useSetIsAddMode();

  const setHomeState = useSetHomeState();
  const setTypeGeneMap = useSetTypeGeneMap();

  let {
    data: dataRegistry,
    isError: isErrorRegistryData,
    isLoading: isLoadingRegistryData,
    error: errorRegistryData
  } = useReadRegistryData({
    registryId,
  });

  let { data: registry } = useReadRegistry(registryId);

  const handleOnCloseAddMode = () => {
    setIsAddMode({ isAddMode: false });
  };

  const handleOnGridReady = () => {
    setIsGridReady(true);
  };

  React.useEffect(() => {
    if (gridRef?.current?.api) {
      const columnDefs: IRegistryColDef[] = gridRef.current.api.getColumnDefs();

      columnDefs.forEach((colDef: IRegistryColDef) => {
        const colMeta = colDef?.meta as IRegistryDataColumn;
        colDef.editable = isEditMode;
        try {
          if (colMeta.type === RegistryDataColumnTypeEnum.calc) {
            colDef.editable = false;
          } else if (colMeta.type === RegistryDataColumnTypeEnum.calc_on_read) {
            colDef.editable = false;
          } else if (colMeta.type === RegistryDataColumnTypeEnum.calc_on_write) {
            colDef.editable = false;
          } else if (colMeta.is_never_edit) {
            colDef.editable = false;
          }
        } catch (e) {
          console.log(e);
        }
      });

      gridRef.current.api!.setColumnDefs(columnDefs);
    }
  }, [isEditMode]);

  React.useEffect(() => {
    const isLeadOpenUpdate = isGeneMapView || isCaseView;
    set_isLeafOpen(!!isLeadOpenUpdate);
  }, [isCaseView, isGeneMapView]);

  React.useEffect(() => {
    if (registry) {
      if (registry.title?.toLowerCase().includes("bloom")) {
        setTypeGeneMap({ typeGeneMap: GeneTypeEnum.bloom });
      } else if (registry.title?.toLowerCase().includes("rothmund")) {
        setTypeGeneMap({ typeGeneMap: GeneTypeEnum.rothmund });
      } else if (registry.title?.toLowerCase().includes("werner")) {
        setTypeGeneMap({ typeGeneMap: GeneTypeEnum.werner });
      } else {
        setTypeGeneMap({ typeGeneMap: GeneTypeEnum.xiagibbs });
      }
    }
  }, [registry, setTypeGeneMap]);

  React.useEffect(() => {
    setHomeState();
    setIsGridReady(false);
  }, [registryId, setHomeState]);

  return (
    <>
      <Stack w="full" h="full" spacing={0} overflow="hidden">
        {/* tabs and toolbar */}
        <HStack justifyContent={"space-between"} flexWrap={"wrap"} py="4">
          <QuickNavigator
            zIndex="2"
            gridRef={gridRef}
            isGridReady={isGridReady}
            registry={registry}
            px={"4"}
          ></QuickNavigator>
          {gridRef && gridRef.current && gridRef.current.api ? (
            <RegistryToolbar
              gridRef={gridRef}
              isAllowAdd={registry?.is_allow_add}
              isAllowEdit={registry?.is_allow_edit}
            ></RegistryToolbar>
          ) : (
            <HStack spacing={6} px={"4"}>
              <Skeleton h="8" w="8"></Skeleton>
              <Skeleton h="8" w="8"></Skeleton>
              <Skeleton h="8" w="8"></Skeleton>
              <Skeleton h="8" w="32"></Skeleton>
            </HStack>
          )}
        </HStack>

        {isCaseView && (
          <Box>
            <RegistryFilterMode
              gridRef={gridRef}
              isGridReady={isGridReady}
            ></RegistryFilterMode>
          </Box>
        )}

        {isGeneMapView && (
          <Box>
            <GeneMapDateBar></GeneMapDateBar>
          </Box>
        )}

        {isEditMode && (
          <Box>
            <RegistryEditMode
              gridRef={gridRef}
              isGridReady={isGridReady}
            ></RegistryEditMode>
          </Box>
        )}

        {/* data canvas */}
        <Stack
          minH={"1px"}
          flex="1"
          position={"relative"}
          overflow={isLeafOpen ? "auto" : "hidden"}
          spacing={0}
          borderTop={"1px"}
          borderColor="border"
        >
          <Flex
            marginTop={isLeafOpen ? "0" : "-500px"}
            w="full"
            h={"500px"}
            minH={"500px"}
            transition={"all .2s ease-in"}
          >
            <RegistryLeaf gridRef={gridRef}></RegistryLeaf>
          </Flex>

          <Box
            position="relative"
            overflow={isLeafOpen ? "unset" : "hidden"}
            // Add to allow table to fill more space
            minH={isLeafOpen ? "50vh" : "full"}
            // h="full"
          >
            <Flex
              h="full"
              minW={isPaneOpen ? "calc(100% - 700px)" : "100%"}
              w={isPaneOpen ? "45%" : "100%"}
              transition={"all .2s ease-in"}
              position="relative"
            >
              <CoreDataGridContainer
                gridRef={gridRef}
                data={dataRegistry}
                isLoading={isLoadingRegistryData}
                isError={isErrorRegistryData}
                errorCode={errorRegistryData?.response?.status}
                errorDescription={errorRegistryData?.response?.data.detail}
                setIsGridReady={handleOnGridReady}
                isRegistry={true}
                registryId={registryId}
              ></CoreDataGridContainer>
            </Flex>

            <Flex
              top="0"
              position="absolute"
              transform={isPaneOpen ? "translateX(-100%)" : "translateX(0%)"}
              // Need to make it not extend on overlfow x when not open but leaf is
              w={!isPaneOpen && isLeafOpen ? "0" : "55%"}
              left="100%"
              bottom="0"
              bg="bg.main"
              transition={"all .2s ease-in"}
              maxW="700px"
              shadow="lg"
              overflow="hidden"
            >
              <RegistryPane
                isGridReady={isGridReady}
                gridRef={gridRef}
              ></RegistryPane>
            </Flex>
          </Box>
        </Stack>
      </Stack>

      {isAddMode && (
        <Box>
          <AddRecordModal
            isOpen={true}
            registryId={registryId}
            onClose={handleOnCloseAddMode}
          ></AddRecordModal>
        </Box>
      )}
    </>
  );
};

export default RegistryDataGrid;
